/* ••[1]••••••••••••••••••••••••• GlobalEventCalendarListController.ts •••••••••••••••••••••••••••••• */

/* ••[2]•••••••••• Components ••••••••••••••• */
import "../components/event-list/index";
import { CCCEventCalendar } from "../components/calendar/index";
import { CCCEventList } from "../components/event-list/index";
import { CCCPanel } from "../components/panel/panel";
import { CCCSelectSingle } from "../components/forms/select-single";
/* ••[2]•••••••••• Interfaces ••••••••••••••• */
import Event, { EventPaginated } from "../core/interfaces/Event";
import SelectItem from "../core/interfaces/SelectItem";
import { PageDirection, SortOption } from "../core/interfaces/Pagination";
import { onDemandEventFilter } from "../core/interfaces/Event";

/* ••[2]•••••••••• Models ••••••••••••••• */
import EventModel from "../models/EventModel";

/* ••[2]•••••••••• Controllers ••••••••••••••• */
import VideoThumbnailController from "./VideoThumbnailController";

interface EventSortOptions {
  LATEST: SelectItem;
  OLDEST: SelectItem;
}
type WindowWithInitFlag = Window & {
  onDemandEventListInitialized?: boolean;
};

export default class OnDemandEventListController {
  private $calendar: CCCEventCalendar = null;
  private $eventList: CCCEventList = null;
  private $panel: CCCPanel;
  private thumbnailCache: { [eventId: string]: HTMLImageElement } = {};
  private allEvents: Array<Event> = null;
  private readonly SORT_OPTIONS: EventSortOptions = {
    LATEST: { id: 0, name: "Latest" },
    OLDEST: { id: 1, name: "Oldest" },
  };
  private readonly defaultSort: SelectItem = this.SORT_OPTIONS.LATEST;
  private videoThumbnailController = new VideoThumbnailController();
  currentSortOption: SelectItem = this.defaultSort;

  constructor(calendarAnchor: string, listAnchor: string, panelAnchor: string) {
    try {
      this.$calendar = document.querySelector(calendarAnchor);
      this.$panel = document.querySelector(panelAnchor);
      this.$eventList = document.querySelector(listAnchor);
      if (!this.$calendar || !this.$panel || !this.$eventList) {
        throw new Error(
          `Global Event Calendar List - ${calendarAnchor}, ${panelAnchor} or ${listAnchor} not found`
        );
      }
      this.$panel.style.display = "none";
    } catch (error) {
      console.log(error);
    }
  }

  public async init(): Promise<void> {
    const win = window as WindowWithInitFlag;
    if (win.onDemandEventListInitialized) {
      console.log(
        "OnDemandEventListController already initialized, skipping..."
      );
      return;
    }
    win.onDemandEventListInitialized = true;

    if (this.$panel && this.$eventList) {
      this.$panel.style.display = "block";
      this.$eventList.display(true);
      this.setupOnDemandTabs();
      this.hideUnusedElements();
      this.setupComponents();
    }
  }

  private hideUnusedElements() {
    const noCatNotice = document.querySelector(".Empty");
    const globalEventsPanel = document.querySelector("#GlobalEventsPanel");
    const addEventButton = document.querySelector(".category__cta > a.btn");
    noCatNotice.setAttribute("hidden", "true");
    addEventButton.setAttribute("style", "display: none");
    globalEventsPanel.setAttribute("style", "display: none");
  }

  private setupComponents(): void {
    this.$calendar.setLoading(false);
    this.setupSortSelect();
    this.setupEventList();

    this.allEvents = null;
    this.$eventList.init();
  }

  private setupSortSelect() {
    const sortSelect = this.$eventList.shadowRoot.querySelector(
      "#sortSelect"
    ) as CCCSelectSingle;
    if (sortSelect) {
      const sortOptions = [this.SORT_OPTIONS.LATEST, this.SORT_OPTIONS.OLDEST];
      sortSelect.setKeyState("options", sortOptions);
      sortSelect.setValue(this.defaultSort);
      sortSelect.setAttribute("value", this.defaultSort.name);

      sortSelect.onItemSelect((value: SelectItem) => {
        if (!this.allEvents) return;
        if (this.currentSortOption.id === value.id) return;
        this.currentSortOption = value;

        this.$eventList.init();
      });

      // Force Latest on tab change
      this.$eventList.onViewChange?.(() => {
        sortSelect.setValue(this.defaultSort);
      });
    }
  }

  private sortEvents(events: Array<Event>): Array<Event> {
    return [...events].sort((a, b) => {
      const aDate = new Date(a.dateEnd).getTime();
      const bDate = new Date(b.dateEnd).getTime();

      return this.currentSortOption.id === this.SORT_OPTIONS.LATEST.id
        ? bDate - aDate
        : aDate - bDate; // (lower dates at top)
    });
  }

  private setupOnDemandTabs(): void {
    const viewOptions = this.getOnDemandViewOptions();
    this.$eventList.setViewOptions(viewOptions);
  }

  private getOnDemandViewOptions(): Array<SelectItem> {
    return Object.values(onDemandEventFilter).map((filter, index) => ({
      id: index,
      name: filter,
    }));
  }

  private setupEventList(): void {
    this.$eventList.onFetchEvents(
      (
        startDate: string,
        endDate: string,
        myEvents: boolean,
        sort = this.currentSortOption?.id === this.SORT_OPTIONS.LATEST.id
          ? SortOption.asc
          : SortOption.desc,
        pageSize = -1,
        page = -1
      ) => {
        this.$eventList.setLoading(true);
        pageSize = 200;
        if (this.allEvents) {
          const sortedEvents = this.sortEvents(this.allEvents);
          this.$eventList.setLoading(false);
          this.$eventList.setEvents(sortedEvents, myEvents);
          this.videoThumbnailController.setUpVideoThumbnails(
            sortedEvents,
            "#EventList"
          );
          return;
        }
        setTimeout(() => {
          const isUAT = window.location.hostname.includes("-uat");
          const onDemandCategoryId = isUAT ? 734 : 662;

          EventModel.getEventsCached(
            startDate,
            endDate,
            startDate + "-" + endDate + (page > 0 ? "-" + page.toString() : ""),
            sort,
            pageSize,
            page,
            onDemandCategoryId
          )
            .then((result: Array<Event> | EventPaginated) => {
              this.$eventList.setLoading(false);
              if ("pagination" in result) {
                const nextPage = result.pagination.has(PageDirection.next)
                  ? Number(result.pagination.get(PageDirection.next).page)
                  : page + 1;
                if (this.$eventList.getCurrentPage() !== nextPage - 1) {
                  this.$eventList.hasMore(result.events);
                } else {
                  this.allEvents = result.events;
                  const sortedEvents = this.sortEvents(result.events);
                  this.$eventList.setPagination(result.pagination);
                  this.$eventList.setEvents(sortedEvents, myEvents);
                  this.videoThumbnailController.setUpVideoThumbnails(
                    sortedEvents,
                    "#EventList"
                  );
                }
              } else {
                this.allEvents = result;
                const sortedEvents = this.sortEvents(result);
                this.$eventList.setEvents(sortedEvents, myEvents);
                this.videoThumbnailController.setUpVideoThumbnails(
                  sortedEvents,
                  "#EventList"
                );
              }
            })
            .catch((error) => {
              console.log(error);
            });
        }, 200);
      }
    );
  }
}
